<template>
  <div id="module-notifications-parent">
    <div class="d-flex justify-center align-center h-100">
      <v-progress-linear
        color="mpBlue"
        height="2"
        indeterminate
        rounded
        class="w-10"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mountRemoteByComponent } from "@/lib/config/spa/register-remote"; 
import { AppConfig } from "@/lib/config";
import keycloak from "@/lib/plugins/keycloak-config";

// Types
interface Props {
  drawer?: { close: () => void }
  onUpdateUnreadCount?: (count: number) => void
  notificationList?: Notification[]
}

export default defineComponent({
  name: "NotificationsDrawer",

  // Props
  props: {
    drawer: {
      type: Object as () => { close: () => void } | undefined,
      default: undefined
    },
    notificationList: {
      type: Array as () => Notification[] | undefined,
      default: undefined
    },
    onUpdateUnreadCount: {
      type: Function as () => ((count: number) => void) | undefined | unknown,
      default: undefined
    },
  },

  emits: ['close'],

  setup(props: Props, { emit }) {
    let remoteHandle: any = null;
    
    // Callback function to update unread count from remote component
    const updateUnreadCount = (count: number) => {
      props.onUpdateUnreadCount?.(count);
    };
    
    /**
     * Mount the remote module
     * This is done to ensure the notification list is up to date when the drawer is opened
     */
    const mountRemote = async () => {
      // Prepare the remote module
      const module = {
        mountId: "module-notifications-parent",
        component: "NotificationDrawer",
        remoteUrl: `${AppConfig.MARKETING_NEW_APPLICATION_URL}/remoteEntry.js?v=${Date.now()}`,
        remoteName: "marketing",
        modulePath: "./marketingModule",
        mountParentSelector: null,
        mountProps: {
          notifications: props.notificationList,
          drawer: props.drawer,
          onUpdateUnreadCount: updateUnreadCount,
          disableNavigation:true
        }
      }
      // Mount the remote module
      try {
        remoteHandle = await mountRemoteByComponent({ ...module as any });
      } catch (err) {
        console.error("Failed to mount remote:", err);
      }
    };
    
    /**
     * Unmount the remote module
     * This is done to ensure the remote module is unmounted when the drawer is closed
     */
    const unmountRemote = async () => {
      try {
        if (remoteHandle?.unmount) {
          await remoteHandle.unmount();
        }
      } catch (err) {
        console.warn("remote unmount failed", err);
      }
    };

    return {
      unmount: unmountRemote,
      mount: mountRemote,
    };
  },
});
</script>

